import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import './Header.css';

interface HeaderProps {
  currentView?: 'upload' | 'trends' | 'reports';
  onViewChange?: (view: 'upload' | 'trends' | 'reports') => void;
}

export const Header: React.FC<HeaderProps> = ({ currentView, onViewChange }) => {
  const { user, logout } = useSimpleAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="app-header">
      <div className="header-container">
        <div className="header-left">
          <Link to="/" className="logo-link">
            <h1 className="logo">📊 UnderstandMyResults</h1>
          </Link>
        </div>

        <div className="header-right">
          {user ? (
            <>
              {/* Navigation tabs for authenticated users */}
              {onViewChange && (
                <nav className="nav-tabs">
                  <button
                    onClick={() => onViewChange('upload')}
                    className={`nav-tab ${currentView === 'upload' ? 'active' : ''}`}
                  >
                    📤 Upload
                  </button>
                  <button
                    onClick={() => onViewChange('trends')}
                    className={`nav-tab ${currentView === 'trends' ? 'active' : ''}`}
                  >
                    📊 Trends
                  </button>
                  <button
                    onClick={() => onViewChange('reports')}
                    className={`nav-tab ${currentView === 'reports' ? 'active' : ''}`}
                  >
                    📋 Reports
                  </button>
                </nav>
              )}

              {/* User menu */}
              <div className="user-menu-container" ref={menuRef}>
                <button
                  className="user-menu-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <span className="user-avatar">
                    {user.firstName ? user.firstName[0].toUpperCase() : user.email[0].toUpperCase()}
                  </span>
                  <span className="user-name">{user.fullName || user.email}</span>
                  <svg className="chevron-icon" width="12" height="8" viewBox="0 0 12 8">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                </button>

                {showUserMenu && ReactDOM.createPortal(
                  <div 
                    ref={dropdownRef}
                    className="user-menu-dropdown"
                    style={{
                      position: 'fixed',
                      top: menuRef.current?.getBoundingClientRect().bottom || 0,
                      right: window.innerWidth - (menuRef.current?.getBoundingClientRect().right || window.innerWidth),
                      marginTop: '8px'
                    }}
                  >
                    <div className="user-menu-header">
                      <div className="user-menu-email">{user.email}</div>
                      {!user.emailVerified && (
                        <div className="email-unverified-badge">Email not verified</div>
                      )}
                    </div>
                    <div className="user-menu-divider" />
                    <Link to="/account" className="user-menu-item">
                      <svg className="menu-icon" width="16" height="16" viewBox="0 0 16 16">
                        <path d="M8 8a3 3 0 100-6 3 3 0 000 6zM14 14a6 6 0 00-12 0" stroke="currentColor" fill="none" />
                      </svg>
                      Account Settings
                    </Link>
                    <button onClick={handleLogout} className="user-menu-item logout">
                      <svg className="menu-icon" width="16" height="16" viewBox="0 0 16 16">
                        <path d="M10 12L14 8L10 4M14 8H3M6 2H4C2.9 2 2 2.9 2 4V12C2 13.1 2.9 14 4 14H6" stroke="currentColor" fill="none" />
                      </svg>
                      Sign Out
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/auth/login" className="header-button secondary">
                Sign In
              </Link>
              <Link to="/auth/register" className="header-button primary">
                Get Started
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};